import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import ErrorMessage from '@minecraft.error-message';
import { genderIdentityWhiteList } from '../../../shared/config';
import { FiltersNameEnum } from '../../../shared/types';
import { generateSelectOptionsBasedOnField, getFilteredArrayByEnabled } from '../../../shared/utils';
import { FormItem } from '../../../shared/styles';
import { FilterDrawerType, FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { useFiltersLabel } from '../../shared/hooks/useFiltersLabel';
import { GenderAppearance } from '../../FilterDrawerCollection';
import { QUERY_GENDER } from './GenderCategoryGraphQL';

interface Props {
  allowAdditionalIdentifies?: boolean;
  type: FilterDrawerType;
}

const GenderCategory: FC<Props> = ({ allowAdditionalIdentifies, type }) => {
  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_GENDER);

  const { isCategoryMatchedSearch, shouldCategoryFilterBeVisible, assignOptionsToCategoryFilters } =
    useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.genderAppearance);
  const { filtersCounts } = useDrawerFiltersCounts();

  const { genderOptions, genderIdentityOptions } = useMemo(() => {
    const generatedGenderIdentityOptions = generateSelectOptionsBasedOnField(
      _sortBy(getFilteredArrayByEnabled(data?.genderIdentities), 'sortOrder'),
      'name',
      false,
      filtersCounts?.[FiltersCountNameEnum.genderIdentityIds],
      true,
      {
        fieldName: FiltersNameEnum.additionallyIdentifiesAs,
        fieldLabel: filtersLabelOfAudience[FiltersNameEnum.additionallyIdentifiesAs],
      }
    );

    return {
      genderOptions: generateSelectOptionsBasedOnField(
        _sortBy(getFilteredArrayByEnabled(data?.genderAppearances), 'sortOrder'),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.genderAppearanceIds],
        true,
        { fieldName: FiltersNameEnum.gender }
      ),
      genderIdentityOptions: _filter(generatedGenderIdentityOptions, (option) =>
        _includes(genderIdentityWhiteList, option.value)
      ),
    };
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (genderOptions.length > 0 && genderIdentityOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.gender]: [...genderOptions, ...(allowAdditionalIdentifies ? genderIdentityOptions : [])],
      });
    }
  }, [genderOptions, genderIdentityOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <>
        {shouldCategoryFilterBeVisible(FiltersNameEnum.gender) && (
          <FormItem $fullWidth $marginValue={2}>
            <GenderAppearance name={FiltersNameEnum.gender} label={filtersLabelOfAudience[FiltersNameEnum.gender]} />
          </FormItem>
        )}
      </>
    )
  );
};

export default GenderCategory;
